import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import Navbar from '@/pages/home/components/Navbar';
import MobileBottomNav from '@/components/feature/MobileBottomNav';

// OAuth 재인증 왕복용 짧은 수명 intent. 이동 전 현재 user.id를 저장하고,
// 복귀 후 새 세션의 user.id와 비교해 원래 계정인지 확인한다.
const REAUTH_INTENT_KEY = 'lottolab:reauth-intent';
const REAUTH_INTENT_TTL_MS = 5 * 60 * 1000;

interface ReauthIntent {
  userId: string;
  expiresAt: number;
}

export default function AccountPage() {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const [nickname, setNickname] = useState(profile?.nickname || '');
  const [nicknameSaving, setNicknameSaving] = useState(false);
  const [nicknameSaved, setNicknameSaved] = useState(false);
  const [nicknameError, setNicknameError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  // 회원 탈퇴 상태
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // OAuth 재인증 완료(원래 계정 일치) 여부 — UI 안내용이며, 최종 판정은 Edge Function의 amr 검증
  const [oauthReauthDone, setOauthReauthDone] = useState(false);

  useEffect(() => {
    if (profile?.nickname) {
      setNickname(profile.nickname);
    }
  }, [profile?.nickname]);

  // OAuth 재인증 복귀: sessionStorage의 reauth intent를 검증한다.
  // intent는 성공(일치)/불일치/만료 시 소비·제거된다.
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    let intent: ReauthIntent | null = null;
    try {
      const raw = sessionStorage.getItem(REAUTH_INTENT_KEY);
      if (raw) intent = JSON.parse(raw) as ReauthIntent;
    } catch {
      intent = null;
    }
    if (!intent) return;

    // intent는 항상 소비 후 제거 (성공/불일치/만료 모두)
    try {
      sessionStorage.removeItem(REAUTH_INTENT_KEY);
    } catch {
      // best-effort
    }

    const expired =
      !intent.userId ||
      typeof intent.expiresAt !== 'number' ||
      Date.now() > intent.expiresAt;
    if (expired) return;

    if (userId !== intent.userId) {
      // 다른 계정을 선택한 경우 → 탈퇴 흐름 취소 + 명확한 오류
      setOauthReauthDone(false);
      setDeleteOpen(true);
      setDeleteError('다른 계정으로 로그인되었습니다. 탈퇴하려는 계정으로 다시 로그인해주세요.');
    } else {
      // 원래 계정과 일치 → 재인증 완료로 표시(자동 삭제하지 않음)
      setOauthReauthDone(true);
      setDeleteOpen(true);
      setDeleteError('');
    }
  }, [userId]);

  if (!user) {
    navigate('/login');
    return null;
  }

  // 비밀번호 계정은 provider='email', OAuth 계정은 'google'/'kakao'
  const provider = user.app_metadata?.provider || 'email';
  const isPasswordAccount = provider === 'email';
  const providerName = provider === 'google' ? 'Google' : provider === 'kakao' ? '카카오' : '이메일';

  const handleNicknameSave = async () => {
    setNicknameError('');
    setNicknameSaved(false);
    if (!nickname.trim()) {
      setNicknameError('닉네임을 입력해주세요.');
      return;
    }
    setNicknameSaving(true);
    try {
      // 반환 행으로 대상 존재 여부를 확인해 실패를 성공으로 표시하지 않는다
      const { data: updated, error } = await supabase
        .from('profiles')
        .update({ nickname: nickname.trim() })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();

      if (error || !updated) {
        if (import.meta.env.DEV) {
          console.error('[Account] 닉네임 저장 실패:', error?.message ?? 'no row');
        }
        setNicknameError('닉네임 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // DB 저장 성공 후에만 성공 UI 표시
      setNicknameSaved(true);
      setTimeout(() => setNicknameSaved(false), 2000);
      try {
        await refreshProfile();
      } catch {
        // 프로필 새로고침 실패는 저장 성공 판정에 영향 없음
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[Account] 닉네임 저장 예외:', err);
      }
      setNicknameError('닉네임 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setNicknameSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSaved(false);

    // 일반 변경은 현재 비밀번호 재인증을 반드시 요구
    if (!currentPassword.trim()) {
      setPasswordError('현재 비밀번호를 입력해주세요.');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError('새 비밀번호를 입력해주세요.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setPasswordSaving(true);
    try {
      // 재인증: 현재 비밀번호 검증 성공 후에만 변경 허용
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword,
      });
      if (signInError) {
        setPasswordError('현재 비밀번호가 올바르지 않습니다.');
        setPasswordSaving(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordSaved(true);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setTimeout(() => setPasswordSaved(false), 2000);
    } catch {
      setPasswordError('비밀번호 변경에 실패했습니다.');
    }
    setPasswordSaving(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');

    // ── 재인증 ──
    // 비밀번호 계정: signInWithPassword 재인증 성공 후 발급된 세션으로 탈퇴를 호출한다.
    if (isPasswordAccount) {
      if (!deletePassword.trim()) {
        setDeleteError('본인 확인을 위해 현재 비밀번호를 입력해주세요.');
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: deletePassword,
      });
      if (signInError) {
        setDeleteError('현재 비밀번호가 올바르지 않습니다.');
        return;
      }
    }

    setDeleting(true);
    try {
      // 서버(delete-my-account Edge Function)에서만 실제 삭제를 수행.
      // 최근 재인증(amr) 판정은 서버가 수행하므로 프론트 상태만으로 삭제되지 않는다.
      const { data, error } = await supabase.functions.invoke('delete-my-account', {
        body: {},
      });

      if (error) {
        let msg = '회원 탈퇴 처리 중 오류가 발생했습니다. 다시 시도해주세요.';
        try {
          const ctx = (error as { context?: { json?: () => Promise<{ message?: string }> } }).context;
          if (ctx?.json) {
            const body = await ctx.json();
            if (body?.message) msg = body.message;
          }
        } catch {
          // fallback message 사용
        }
        setDeleteError(msg);
        setDeleting(false);
        return;
      }

      // 서버 성공 응답을 받은 뒤에만 세션 정리 + 이동
      if (data?.success === true) {
        setDeleting(false);
        setDeleteOpen(false);
        setOauthReauthDone(false);
        await signOut();
        navigate('/', { replace: true });
      } else if (data?.error === 'reauth_required') {
        // 최근 재인증이 없음 → 재인증 유도 (실패 시 성공으로 표시하지 않음)
        setOauthReauthDone(false);
        setDeleteError(data?.message || '본인 인증이 필요합니다. 다시 로그인해주세요.');
        setDeleting(false);
      } else {
        setDeleteError(data?.message || '회원 탈퇴 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
        setDeleting(false);
      }
    } catch {
      setDeleteError('회원 탈퇴 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      setDeleting(false);
    }
  };

  const handleOAuthReauth = async () => {
    setDeleteError('');
    const oauthProvider = provider as 'google' | 'kakao';

    // 이동 전 현재 user.id를 짧은 수명 intent로 저장
    try {
      sessionStorage.setItem(
        REAUTH_INTENT_KEY,
        JSON.stringify({
          userId: user.id,
          expiresAt: Date.now() + REAUTH_INTENT_TTL_MS,
        } satisfies ReauthIntent),
      );
    } catch {
      // sessionStorage 사용 불가 시에도 진행 (서버 amr가 최종 판정)
    }

    // Google: 계정 선택 강제(select_account). 비밀번호 강제 재인증이 아니다.
    // Kakao: prompt=login 으로 재로그인 유도.
    const queryParams =
      oauthProvider === 'google'
        ? { prompt: 'select_account' }
        : { prompt: 'login' };

    const { error } = await supabase.auth.signInWithOAuth({
      provider: oauthProvider,
      options: {
        redirectTo: `${window.location.origin}/account`,
        queryParams,
      },
    });
    if (error) {
      // OAuth 시작 실패 시 intent 제거
      try {
        sessionStorage.removeItem(REAUTH_INTENT_KEY);
      } catch {
        // best-effort
      }
      setDeleteError(error.message);
    }
  };

  const openDelete = () => {
    setDeletePassword('');
    setDeleteError('');
    setOauthReauthDone(false);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteError('');
    setOauthReauthDone(false);
    try {
      sessionStorage.removeItem(REAUTH_INTENT_KEY);
    } catch {
      // best-effort
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-2xl text-foreground-800 mb-1">계정 설정</h1>
            <p className="text-foreground-400 text-sm">{user.email} 계정 정보를 관리하세요.</p>
          </div>

          <div className="bg-background-50 rounded-2xl p-6 border border-background-200 mb-4">
            <h3 className="font-heading text-base text-foreground-800 mb-4">닉네임 변경</h3>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">닉네임</label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="별명을 입력해주세요"
                  maxLength={20}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                onClick={handleNicknameSave}
                disabled={nicknameSaving}
                className="px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {nicknameSaving ? '저장 중...' : '저장'}
              </button>
            </div>
            {nicknameError && (
              <p className="mt-2 text-xs text-accent-600">{nicknameError}</p>
            )}
            {nicknameSaved && (
              <p className="mt-2 text-xs text-secondary-600 flex items-center gap-1">
                <i className="ri-check-line"></i> 닉네임이 저장되었습니다.
              </p>
            )}
          </div>

          <div className="bg-background-50 rounded-2xl p-6 border border-background-200 mb-4">
            <h3 className="font-heading text-base text-foreground-800 mb-4">비밀번호 변경</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">현재 비밀번호</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="현재 비밀번호를 입력해주세요"
                  autoComplete="current-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">새 비밀번호</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="6자 이상 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">새 비밀번호 확인</label>
                <input
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                onClick={handlePasswordChange}
                disabled={passwordSaving}
                className="px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {passwordSaving ? '변경 중...' : '비밀번호 변경'}
              </button>
              {passwordError && (
                <p className="text-xs text-accent-600">{passwordError}</p>
              )}
              {passwordSaved && (
                <p className="text-xs text-secondary-600 flex items-center gap-1">
                  <i className="ri-check-line"></i> 비밀번호가 변경되었습니다.
                </p>
              )}
            </div>
          </div>

          <div className="bg-background-50 rounded-2xl p-6 border border-accent-200">
            <h3 className="font-heading text-base text-accent-700 mb-2">위험 구역</h3>
            <p className="text-foreground-400 text-xs mb-4">
              회원 탈퇴 시 저장된 번호, 약관 동의 이력, 프로필 정보가 모두 영구 삭제되며 복구할 수 없습니다.
            </p>
            <button
              onClick={openDelete}
              className="px-5 py-2.5 rounded-xl border-2 border-accent-300 text-accent-600 hover:bg-accent-50 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              회원 탈퇴
            </button>
          </div>
        </div>
      </div>

      {/* 탈퇴 확인 모달 */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeDelete(); }}
        >
          <div className="bg-background-50 rounded-2xl w-full max-w-md">
            <div className="flex items-center gap-3 p-5 border-b border-background-200">
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-alert-line text-lg text-accent-600"></i>
              </div>
              <div>
                <h3 className="font-heading text-base text-foreground-800">회원 탈퇴</h3>
                <p className="text-xs text-foreground-400">본인 확인 후 모든 데이터가 삭제됩니다.</p>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {isPasswordAccount ? (
                <div>
                  <label className="block text-xs text-foreground-500 mb-1.5">현재 비밀번호</label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="본인 확인을 위해 비밀번호를 입력해주세요"
                    autoComplete="current-password"
                    className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-400"
                  />
                </div>
              ) : oauthReauthDone ? (
                <div className="p-3 rounded-lg bg-secondary-50 border border-secondary-200 flex items-start gap-2">
                  <i className="ri-check-line text-secondary-600 text-sm mt-0.5"></i>
                  <p className="text-xs text-secondary-700">
                    재인증이 완료되었습니다. 아래 '탈퇴하기' 버튼을 눌러 삭제를 완료해주세요.
                  </p>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-background-100 border border-background-200">
                  <p className="text-xs text-foreground-600">
                    계정 삭제를 위해 <strong className="text-foreground-800">{providerName}</strong> 계정으로
                    다시 로그인(재인증)해야 합니다.
                  </p>
                  <button
                    type="button"
                    onClick={handleOAuthReauth}
                    className="mt-3 px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                  >
                    {providerName} 재로그인
                  </button>
                </div>
              )}

              {deleteError && (
                <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                  <p className="text-accent-700 text-sm">{deleteError}</p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-300 text-sm text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting || (!isPasswordAccount && !oauthReauthDone)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                >
                  {deleting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      삭제 중...
                    </>
                  ) : (
                    '탈퇴하기'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="md:hidden h-16"></div>
      <MobileBottomNav />
    </div>
  );
}